/**
 * Clipboard Paste Utility
 *
 * Parses tab-separated text (from Excel/Google Sheets) into dictionary entries.
 * Extracted as a pure function for testability.
 *
 * Reference: Data_Dictionary_Upload_Design.md
 */

export interface ParsedDictionaryEntry {
  column_name: string;
  description: string;
  business_rule?: string;
  expected_format?: string;
  joins_to?: string;
  owner?: string;
  sensitivity?: 'none' | 'pii' | 'phi' | 'confidential';
}

/**
 * Flexible header name mappings.
 * Each key is the canonical field name; values are acceptable header variants.
 */
const HEADER_ALIASES: Record<string, string[]> = {
  column_name: ['column_name', 'name', 'field', 'column'],
  description: ['description', 'desc', 'definition'],
  business_rule: ['business_rule', 'rule', 'business rule'],
  expected_format: ['expected_format', 'format', 'expected format'],
  joins_to: ['joins_to', 'joins to', 'join', 'related_table'],
  owner: ['owner', 'data_owner', 'data owner'],
  sensitivity: ['sensitivity', 'classification', 'data_class'],
};

/**
 * Find the column index for a canonical field name based on flexible header matching.
 */
function findHeaderIndex(headers: string[], fieldName: string): number {
  const aliases = HEADER_ALIASES[fieldName] || [fieldName];
  return headers.findIndex(h => aliases.includes(h));
}

/**
 * Parse tab-separated clipboard text into dictionary entries.
 *
 * Expects:
 * - First row is headers (tab-separated)
 * - Subsequent rows are data (tab-separated)
 * - At minimum: column_name and description headers must be present
 *
 * @param text - Raw clipboard text
 * @returns Array of parsed entries (empty if parse fails)
 */
export function parseClipboardText(text: string): ParsedDictionaryEntry[] {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];

  // Parse header row
  const headers = lines[0].split('\t').map(h => h.trim().toLowerCase());

  // Find required columns
  const nameIdx = findHeaderIndex(headers, 'column_name');
  const descIdx = findHeaderIndex(headers, 'description');

  if (nameIdx === -1 || descIdx === -1) return [];

  // Find optional columns
  const ruleIdx = findHeaderIndex(headers, 'business_rule');
  const formatIdx = findHeaderIndex(headers, 'expected_format');
  const joinsIdx = findHeaderIndex(headers, 'joins_to');
  const ownerIdx = findHeaderIndex(headers, 'owner');
  const sensitivityIdx = findHeaderIndex(headers, 'sensitivity');

  const validSensitivities = ['none', 'pii', 'phi', 'confidential'];

  const entries: ParsedDictionaryEntry[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split('\t').map(c => c.trim());
    const colName = cols[nameIdx];
    const desc = cols[descIdx];
    if (!colName || !desc) continue;

    const rawSensitivity = sensitivityIdx >= 0 ? cols[sensitivityIdx]?.toLowerCase() : undefined;

    entries.push({
      column_name: colName,
      description: desc,
      business_rule: ruleIdx >= 0 ? cols[ruleIdx] || undefined : undefined,
      expected_format: formatIdx >= 0 ? cols[formatIdx] || undefined : undefined,
      joins_to: joinsIdx >= 0 ? cols[joinsIdx] || undefined : undefined,
      owner: ownerIdx >= 0 ? cols[ownerIdx] || undefined : undefined,
      sensitivity: rawSensitivity && validSensitivities.includes(rawSensitivity)
        ? rawSensitivity as ParsedDictionaryEntry['sensitivity']
        : undefined,
    });
  }
  return entries;
}
